// スマホの商品カードの画像（snippets/gramorph-card-gallery.liquid）を動かす。
// 指でめくる動きは CSS の scroll-snap。ここでは、いま見ている点の更新と、約4秒ごとの自動の切り替えをする。
// JS が読めないときは、指でめくるだけになる。
// 一覧の絞り込みやおすすめ商品で後から入るカードにも効くよう、カスタム要素にしている。
if (!customElements.get('gramorph-card-gallery')) {
  const AUTOPLAY_INTERVAL_MS = 4000;
  // 指を離してからタイマーを動かすまで。慣性のスクロールが終わり、選んだ画像を見る時間を残す。次へ送るのは、ここからさらに4〜8秒後
  const TOUCH_HOLD_MS = 4000;
  // 読み込みが遅いときは、その回は送らず、次の回にもう一度確かめる
  const DECODE_TIMEOUT_MS = 10000;
  const autoplayQuery = window.matchMedia('(max-width: 749px) and (prefers-reduced-motion: no-preference)');

  customElements.define(
    'gramorph-card-gallery',
    class GramorphCardGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.card__gallery-track');
        this.dots = this.querySelectorAll('.card__gallery-dot');
        this.slides = this.querySelectorAll('.card__gallery-slide');
        if (!this.track) return;
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.updateDots();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateDots();
        this.setUpAutoplay();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.tearDownAutoplay();
      }

      currentIndex() {
        const width = this.track.clientWidth;
        return width ? Math.round(Math.abs(this.track.scrollLeft) / width) : 0;
      }

      updateDots() {
        if (!this.track.clientWidth) return;
        const current = this.currentIndex();
        this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === current));
      }

      setUpAutoplay() {
        this.onScreen = false;
        this.touching = false;
        this.touchHold = null;
        this.pointerInside = false;
        this.focusInside = false;
        this.failed = new Set();
        this.syncAutoplay = () => this.updateAutoplay();
        this.onTouchStart = () => {
          clearTimeout(this.touchHold);
          this.touchHold = null;
          this.touching = true;
          this.updateAutoplay();
        };
        this.onTouchEnd = () => {
          this.touching = false;
          clearTimeout(this.touchHold);
          this.touchHold = setTimeout(() => {
            this.touchHold = null;
            this.updateAutoplay();
          }, TOUCH_HOLD_MS);
        };
        // 幅の狭い PC のウィンドウ。指のときは pointerenter を見ない（touchstart で止めている）
        this.onPointerEnter = (event) => {
          if (event.pointerType === 'mouse') this.setInteraction('pointerInside', true);
        };
        this.onPointerLeave = (event) => {
          if (event.pointerType === 'mouse') this.setInteraction('pointerInside', false);
        };
        // キーボードのときだけ止める。タップでもフォーカスは入り、戻ったときに止まったままになるため
        this.onFocusIn = () => this.setInteraction('focusInside', this.track.matches(':focus-visible'));
        this.onFocusOut = () => this.setInteraction('focusInside', false);
        this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
        this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
        this.track.addEventListener('pointerenter', this.onPointerEnter);
        this.track.addEventListener('pointerleave', this.onPointerLeave);
        this.track.addEventListener('focusin', this.onFocusIn);
        this.track.addEventListener('focusout', this.onFocusOut);
        autoplayQuery.addEventListener('change', this.syncAutoplay);
        document.addEventListener('visibilitychange', this.syncAutoplay);
        window.addEventListener('pageshow', this.syncAutoplay);
        this.observer = new IntersectionObserver(
          (entries) => {
            this.onScreen = entries[entries.length - 1].isIntersecting;
            this.updateAutoplay();
          },
          { threshold: 0.5 }
        );
        this.observer.observe(this);
      }

      tearDownAutoplay() {
        this.stopTimer();
        clearTimeout(this.touchHold);
        this.touchHold = null;
        this.observer?.disconnect();
        this.track.removeEventListener('touchstart', this.onTouchStart);
        this.track.removeEventListener('touchend', this.onTouchEnd);
        this.track.removeEventListener('touchcancel', this.onTouchEnd);
        this.track.removeEventListener('pointerenter', this.onPointerEnter);
        this.track.removeEventListener('pointerleave', this.onPointerLeave);
        this.track.removeEventListener('focusin', this.onFocusIn);
        this.track.removeEventListener('focusout', this.onFocusOut);
        autoplayQuery.removeEventListener('change', this.syncAutoplay);
        document.removeEventListener('visibilitychange', this.syncAutoplay);
        window.removeEventListener('pageshow', this.syncAutoplay);
      }

      setInteraction(name, value) {
        this[name] = value;
        this.updateAutoplay();
      }

      updateAutoplay() {
        const shouldRun =
          autoplayQuery.matches &&
          this.slides.length > 1 &&
          !navigator.connection?.saveData &&
          this.onScreen &&
          !document.hidden &&
          !this.touching &&
          this.touchHold === null &&
          !this.pointerInside &&
          !this.focusInside;
        if (!shouldRun) {
          this.stopTimer();
        } else if (!this.timer) {
          // カードごとに時刻をずらす。止まったあとにそろって動き出さないよう、始めるたびにずらす
          this.startTimer(AUTOPLAY_INTERVAL_MS + Math.random() * AUTOPLAY_INTERVAL_MS);
        }
      }

      startTimer(delay) {
        this.timer = setTimeout(() => this.showNext(), delay);
      }

      stopTimer() {
        clearTimeout(this.timer);
        this.timer = null;
      }

      // 読み込めなかった画像を飛ばして、次の画像の番号を返す
      nextIndex() {
        const count = this.slides.length;
        const current = this.currentIndex();
        for (let step = 1; step < count; step++) {
          const index = (current + step) % count;
          if (!this.failed.has(index)) return index;
        }
        return current;
      }

      async showNext() {
        const timer = this.timer;
        const next = this.nextIndex();
        // 遅延読み込みの次の画像を、送る直前に読み込む。読み込むまで送らないので、切り替えた直後に空白にならない
        const image = this.slides[next].querySelector('img');
        let ready = false;
        try {
          image.loading = 'eager';
          ready = await Promise.race([
            image.decode().then(() => true),
            new Promise((resolve) => setTimeout(() => resolve(false), DECODE_TIMEOUT_MS)),
          ]);
        } catch (error) {
          this.failed.add(next);
        }
        // 待っている間に止まったら、送らない
        if (this.timer !== timer) return;
        if (ready) {
          // ページは動かさず、帯の中だけを送る。点は scroll のイベントで動く
          this.track.scrollTo({ left: next * this.track.clientWidth, behavior: 'smooth' });
        }
        this.startTimer(AUTOPLAY_INTERVAL_MS);
      }
    }
  );
}
